import React, { useEffect, useRef } from "react";
import versusImage from "./assets/vitrine/templates/templVersus.png";
import storyboardImage from "./assets/vitrine/templates/tmplt_storyboard.png";
import seedImage from "./assets/vitrine/templates/tmplt_Seed.png";
import relayImage from "./assets/vitrine/templates/tmplt_Relay.png";
import symphonyImage from "./assets/vitrine/templates/tmplt_Symphony.png";
import galleryImage from "./assets/vitrine/templates/tmplt_gallery.png";
import headerTemplateImage from "./assets/vitrine/templates/header_template.png";
import weaveYourStoryVideo from "./assets/vitrine/templates/weaveyourstory.mp4";
import storyboardVideo from "./assets/vitrine/templates/Storyboard2.mp4";
import relayVideo from "./assets/vitrine/templates/Relay1.mp4";
import galleryVideo from "./assets/vitrine/templates/galerie.mp4";
import "./TemplatesPage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

const DetailBlock = ({ title, subtitle, image, concept, how, application, result, reverse = false }) => (
  <section className={`tpl-framework${reverse ? " is-reverse" : ""}`}>
    <figure className="tpl-media">
      <img src={image} alt={title} loading="lazy" />
    </figure>
    <div className="tpl-copy">
      <h2>{title}</h2>
      <p className="tpl-tagline">{subtitle}</p>
      <dl>
        {[concept, how, application, result].map((value, index) => (
          <div key={index}>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  </section>
);

const PreviewVideo = ({ src, className = "" }) => {
  const ref = useRef(null);

  const playWithSound = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepPlayingMuted = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  return (
    <div className={`tpl-videoFrame ${className}`.trim()} onMouseEnter={playWithSound} onMouseLeave={keepPlayingMuted}>
      <video ref={ref} controls autoPlay muted loop playsInline preload="auto">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

export default function TemplatesPage() {
  useEffect(() => {
    document.title = "Templates | Weavevo";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="tpl-page">
      <section className="tpl-visualHero" aria-label="Templates">
        <img className="tpl-heroImage" src={headerTemplateImage} alt="" />
        <div className="tpl-heroScrim" />
        <div className="tpl-heroContent">
          <h1>Video is no longer static. It’s alive.</h1>
          <p>Transform raw, isolated footage into perpetual, shared narratives. Select your blueprint, unite your creators, and let intelligent algorithms weave your stories instantly.</p>
        </div>
      </section>

      <section className="tpl-videoSection">
        <PreviewVideo src={weaveYourStoryVideo} previewTime={3.5} />
      </section>

      <div className="tpl-frameworks">
        <DetailBlock
          title="Versus"
          subtitle="Split-screen comparison & dual-stream synchronization."
          image={versusImage}
          concept={'Inspiration & Comparison Model: "Plant a spark and let the community’s interpretation grow in parallel."'}
          how={'We provide a single abstract theme or a "spark," and you interpret it freely. It is designed to capture the "soul" of a subject through direct dual contrast.'}
          application={'Filming what "Silence" or "History" looks like in your local environment, leading to unexpected and poetic artistic shots (ex: Desert Sunrise vs City Sunrise).'}
          result="An expressive, poetic collection of interpretations in synchronized split-screen."
        />

        <DetailBlock
          title="Storyboard"
          subtitle="A strict visual blueprint for perfect shot matching."
          image={storyboardImage}
          concept={'Precision Model: "Define a sequence; every frame is a piece of the puzzle."'}
          how="Contributors follow a strict visual blueprint (script and sketches) to ensure every shot fits perfectly into a pre-defined narrative."
          application="Recreating a specific camera movement or angle in your city to match a sequence filmed on the other side of the world."
          result="A tightly synchronized sequence where shots match across locations."
          reverse
        />

        <section className="tpl-videoSection tpl-videoSectionInner">
          <PreviewVideo src={storyboardVideo} previewTime={5} />
        </section>

        <DetailBlock
          title="Seed"
          subtitle="One spark, endless interpretations."
          image={seedImage}
          concept={'Inspiration Model: "Plant a spark and let the community’s interpretation grow."'}
          how={'We provide a single abstract theme or a "spark," and you interpret it freely. It is designed to capture the "soul" of a subject.'}
          application={'Filming what "Silence" or "History" looks like in your local environment, leading to unexpected and poetic artistic shots.'}
          result="An expressive, poetic collection of interpretations."
        />

        <DetailBlock
          title="relay"
          subtitle="A movement starts here, finishes elsewhere."
          image={relayImage}
          concept={'Continuity Model: "Build a bridge through motion. A gesture starts here and ends elsewhere."'}
          how="Creating a bridge through action. A movement or gesture starts in one location and is seamlessly completed in another."
          application="An athlete running out of the frame in New York and appearing in the next frame running into the streets of Tokyo."
          result="A seamless action handoff that feels like one continuous take."
          reverse
        />

        <section className="tpl-videoSection tpl-videoSectionInner">
          <PreviewVideo src={relayVideo} previewTime={2.8} />
        </section>

        <DetailBlock
          title="Symphony"
          subtitle="Each person captures a different layer."
          image={symphonyImage}
          concept={'Sensory Model: "Unite video, ambient sound, and macro-textures into one harmony."'}
          how="A collaborative breakdown where each person handles a specific sensory layer (video, sound, or textures)."
          application="One person films the wide atmosphere, another records high-quality ambient sounds, and a third focuses on macro-details of surfaces."
          result="A layered piece where visuals, sound, and textures work together."
        />

        <DetailBlock
          title="Galery"
          subtitle="An open call for many perspectives on one subject."
          image={galleryImage}
          concept={'Diversity Model: "One subject, endless eyes. A mosaic of perspectives."'}
          how="An open call for a variety of perspectives on a single subject. There are no rules—just the beauty of different eyes seeing the same thing."
          application="Collecting hundreds of different views of a landmark or event to create a massive visual mosaic or a high-speed montage."
          result="A rich mosaic built from diverse viewpoints."
          reverse
        />

        <section className="tpl-videoSection tpl-videoSectionInner">
          <PreviewVideo src={galleryVideo} previewTime={2} />
        </section>
      </div>

      <section className="tpl-mobileExplore">
        <h2>Unite every team. Keep your video permanently alive.</h2>
        <p>Live event coverage, immersive hospitality showcases, virtual real estate walkthroughs, or distributed retail activations: deploy the Weavevo app across your field teams and creator networks to capture, sync, and publish living video assets in real time.</p>
        <div className="tpl-storeActions">
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="tpl-storeButton">
            <i className="bi bi-google-play" aria-hidden="true" />
            <span>Play Store</span>
          </a>
          <button type="button" className="tpl-storeButton" disabled>
            <i className="bi bi-apple" aria-hidden="true" />
            <span>App Store</span>
          </button>
        </div>
      </section>
    </main>
  );
}
